import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE, PURE_SOULS_URL } from "@/lib/site";

// Per-page title + meta description, written around the keywords each page targets.
export const PAGE_SEO = {
  home: {
    title: "Pet Water Cremation Australia | Aqua Cremation Guide & Providers",
    description:
      "Australia's independent guide to pet water cremation (aqua cremation, alkaline hydrolysis). Understand the gentle, eco friendly process and find verified providers in your state.",
    path: "/",
  },
  whatIs: {
    title: "What Is Water Cremation for Pets? Aqua Cremation Explained",
    description:
      "Water cremation, also called aqua cremation or alkaline hydrolysis, gently returns a pet's body to its natural elements using warm water and alkaline chemistry. Here is how it works.",
    path: "/what-is-water-cremation",
  },
  benefits: {
    title: "Benefits of Pet Water Cremation vs Flame Cremation | Australia",
    description:
      "Compare pet water cremation with flame cremation: up to 90 percent less energy, no direct emissions, and soft natural remains. Explore the benefits with our interactive tool.",
    path: "/benefits",
  },
  process: {
    title: "The Pet Water Cremation Process, Step by Step | Australia",
    description:
      "A calm walkthrough of how pet water cremation works, from respectful transfer and individual identification to the gentle return of your pet's remains.",
    path: "/the-process",
  },
  providers: {
    title: "Pet Water Cremation Providers in Australia | Verified Directory",
    description:
      "Find verified pet water cremation providers across Australia by state, including QLD, NSW, VIC, WA, SA, TAS and NT. Pure Souls is our featured premium provider.",
    path: "/providers",
  },
  faq: {
    title: "Pet Water Cremation FAQ | Cost, Process and Remains | Australia",
    description:
      "Answers to common questions about pet water cremation: what it is, how long it takes, what remains are returned, environmental impact, cost and availability in Australia.",
    path: "/faq",
  },
  articles: {
    title: "Water Cremation Learning Centre | Articles and Guides | Australia",
    description:
      "Educational, evergreen articles on pet water cremation, aqua cremation and sustainable pet aftercare in Australia.",
    path: "/articles",
  },
};

export function breadcrumbLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}

export function faqLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
}

export function articleLd(article) {
  const url = `${SITE_URL}/articles/${article.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.hero_image ? [article.hero_image] : [DEFAULT_OG_IMAGE],
    datePublished: article.published_at,
    dateModified: article.published_at,
    articleSection: article.category,
    inLanguage: "en-AU",
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    author: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: { "@type": "ImageObject", url: DEFAULT_OG_IMAGE },
    },
  };
}

export function processHowToLd(steps) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How Pet Water Cremation Works",
    description:
      "The pet water cremation process, from respectful transfer and individual identification through the gentle water based process to the return of remains to the family.",
    step: steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.title,
      text: s.body,
    })),
  };
}

export const PURE_SOULS_LD = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "Pure Souls Pet Aqua Cremation",
  description:
    "Premium pet water cremation (aqua cremation) services in Townsville, Queensland, focused on compassionate, individual care and sustainable pet aftercare.",
  url: PURE_SOULS_URL,
  image: DEFAULT_OG_IMAGE,
  areaServed: { "@type": "State", name: "Queensland" },
  address: {
    "@type": "PostalAddress",
    addressLocality: "Townsville",
    addressRegion: "QLD",
    addressCountry: "AU",
  },
};

export function providersItemListLd(providers) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Pet Water Cremation Providers in Australia",
    itemListElement: providers.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "LocalBusiness",
        name: p.name,
        description: p.description,
        url: p.website,
        areaServed: p.state,
        address: {
          "@type": "PostalAddress",
          addressRegion: p.state,
          addressCountry: "AU",
        },
      },
    })),
  };
}

export function articlesItemListLd(articles) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Water Cremation Learning Centre",
    itemListElement: articles
      .filter((a) => !a.is_stub)
      .map((a, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${SITE_URL}/articles/${a.slug}`,
        name: a.title,
      })),
  };
}

// Organization + WebSite + WebPage graph — drives Google's site logo in the
// Knowledge Panel, the brand image in search cards, and the sitelinks search
// box. The logo is the square favicon (clear continent silhouette at all
// sizes); the primaryImage is the wider hero used for OG cards.
const BRAND_LOGO = `${SITE_URL}/android-chrome-512x512.png`;

export function homepageLd() {
  const orgId = `${SITE_URL}/#organization`;
  const websiteId = `${SITE_URL}/#website`;
  const imageId = `${SITE_URL}/#primary-image`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": orgId,
        name: SITE_NAME,
        alternateName: "Water Cremation Australia",
        url: SITE_URL,
        logo: {
          "@type": "ImageObject",
          url: BRAND_LOGO,
          width: 512,
          height: 512,
          caption: SITE_NAME,
        },
        image: { "@id": imageId },
        description:
          "Australia's independent educational guide to pet water cremation (aqua cremation, alkaline hydrolysis).",
        areaServed: { "@type": "Country", name: "Australia" },
      },
      {
        "@type": "WebSite",
        "@id": websiteId,
        url: SITE_URL,
        name: SITE_NAME,
        publisher: { "@id": orgId },
        inLanguage: "en-AU",
        potentialAction: {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: `${SITE_URL}/articles?q={search_term_string}`,
          },
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@type": "ImageObject",
        "@id": imageId,
        url: DEFAULT_OG_IMAGE,
        contentUrl: DEFAULT_OG_IMAGE,
        width: 1536,
        height: 1024,
        caption:
          "Australia rendered in clear flowing water — Pet Water Cremation Australia",
      },
      {
        "@type": "WebPage",
        "@id": `${SITE_URL}/#webpage`,
        url: SITE_URL,
        name: PAGE_SEO.home.title,
        description: PAGE_SEO.home.description,
        isPartOf: { "@id": websiteId },
        about: { "@id": orgId },
        primaryImageOfPage: { "@id": imageId },
        inLanguage: "en-AU",
      },
    ],
  };
}
